'use client';

import React, { useEffect, useState } from 'react';
import { cn } from '@/utils/cn';
import { useSelector } from 'react-redux';
import { formatPrice } from '@/utils/number';
import { getImageUrl } from '@/utils/string';
import { currentCurrency, SWIPE } from '@/constants';
import { imageTypes } from '@/constants/types';
import Button from '../Button';
import Icon from '../Icon';
import EmiTable from '../Table/EmiTable';
import EmiTableDesktop from '../Table/EmiTableDesktop';
import ModalWrapper from '../Wrapper/ModalWrapper';
import CloseButton from '../Button/CloseButton';
import TextBase from '../Typography/TextBase';
import ImageComponentV2 from '../Image/ImageComponentV2';
import ModalTitle from '../Typography/ModalTitle';

function EmiTableModal({ onView, onClose, data }) {
  const { bankEmiList } = useSelector((state) => state.checkoutSummary);
  const { minEMIPrice } = useSelector((state) => state.globalSettings);

  // states
  const [openModal, setOpenModal] = useState(onView);
  const [selectedBank, setSelectedBank] = useState(bankEmiList[0]);
  const [openBankList, setOpenBankList] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [tenureDetails, setTenureDetails] = useState(
    selectedBank.tenure_details ?? [],
  );

  /* gesture close for mobile */
  const [translateY, setTranslateY] = useState(0);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (onView) {
      setTimeout(() => {
        setOpenModal(true);
        document.body.style.overflow = 'hidden';
      }, 100);
    } else {
      setOpenModal(false);
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [onView]);

  const handleSearchInputChange = (event) => {
    setSearchTerm(event.target.value.trim());
    const searchValue = event.target.value;
    searchValue.trim();

    if (searchValue !== '' || searchValue !== null) {
      setSelectedBank(searchValue);
      setOpenBankList(true);
    } else {
      setOpenBankList(false);
    }
  };

  useEffect(() => {
    if (selectedBank === '' || selectedBank === null) {
      setOpenBankList(false);
    }
  }, [selectedBank]);

  const filteredBanks = searchTerm
    ? bankEmiList.filter((bank) =>
        bank.name.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    : bankEmiList;

  const handleSelectBank = (bankName) => {
    setSelectedBank(bankName);
    setTenureDetails(bankName?.tenure_details);
  };

  /* gesture close for mobile */
  const handleTranslateY = (value) => {
    setTranslateY(value);
  };

  const handleClose = (state) => {
    if (state === SWIPE) {
      setIsClosing(true);
      setTimeout(() => {
        onClose(false);
        setTranslateY(0);
        setIsClosing(false);
      }, 200);
    } else {
      onClose(false);
    }
  };

  if (bankEmiList.length === 0) {
    return null;
  }

  return (
    <ModalWrapper
      isOpen={openModal}
      onOpenModal={setOpenModal}
      className="flex items-end lg:items-center"
    >
      <div className="w-full lg:w-[1060px] h-auto lg:h-[90vh] mt-0 lg:mt-0 mx-0 lg:mx-8 p-0 lg:p-24 relative rounded-[4px] lg:rounded-[12px] transition-all duration-[400ms]">
        <div className="w-full h-full lg:flex flex-col justify-center items-stretch">
          <CloseButton onClose={handleClose} className="mb-12" />

          <div
            style={{
              transform: `translateY(${translateY}px)`,
              transition: isClosing
                ? 'transform 0.3s ease-out, opacity 0.3s ease-out'
                : 'transform 0.3s ease-out',
            }}
            className="h-full max-h-max lg:max-h-[880px] bg-white lg:max-w-[1060px] p-20 lg:p-24 rounded-t-xl lg:rounded-xl shadow-product-slider-shadow"
          >
            {/* gesture close for mobile */}
            <Button
              type="modal-swipe-close-button"
              onTranslateY={handleTranslateY}
              onClose={handleClose}
            />

            <div className="flex justify-between gap-[10px] mt-16 mb-16 lg:mb-20 lg:mt-0">
              <ModalTitle
                text="EMI Conditions"
                className="text-lg lg:text-xl font-semibold"
              />

              <Icon
                type="close"
                onClick={handleClose}
                className="inline-block lg:hidden size-[24px] lg:size-[32px] cursor-pointer"
              />
            </div>
            <div className="bg-white rounded-xl">
              <TextBase
                text="Select a bank to see the EMI details"
                className="text-center lg:text-left text-coarse-wool font-medium lg:font-semibold leading-20 lg:leading-32 tracking-[0.28px] lg:trackcing-[0.32px] mb-12 lg:mb-16"
              />

              <div className="relative max-w-[592px]">
                <div
                  className={cn(
                    'w-full max-w-[592px] h-[40px] lg:h-[48px] flex items-center justify-center bg-white p-0 lg:py-4 px-8 lg:px-12 border border-atelier rounded-[28px] gap-[12px] mb-16',
                    openBankList && 'border-2',
                  )}
                >
                  <Button
                    type="link"
                    className="flex items-center justify-center ml-4 !p-0"
                  >
                    <Icon
                      type="search"
                      className="size-[24px] lg:size-[32px]"
                      color="#49525E"
                    />
                  </Button>

                  <input
                    className="w-full border-none bg-transparent text-coarse-wool text-16 font-encode-sans font-medium leading-20 tracking-[0.32px] py-1 placeholder:text-lightslategray placeholder:text-14 lg:placeholder:text-18 placeholder:font-normal placeholder:font-encode-sans placeholder:leading-28 placeholder:tracking-[0.36px] outline-none focus:outline-none "
                    type="text"
                    name="search"
                    value={selectedBank?.name}
                    onChange={(e) => handleSearchInputChange(e)}
                    placeholder=""
                  />
                  <Button
                    type="link"
                    className={cn('flex items-center justify-center ml-4 !p-0')}
                    onClick={() => setOpenBankList((prevValue) => !prevValue)}
                  >
                    <Icon
                      type="angle-down"
                      className={cn(
                        'size-[20px] lg:size-[32px]',
                        openBankList && 'rotate-180',
                      )}
                      color="#49525E"
                    />
                  </Button>
                </div>

                <div
                  className={cn(
                    'rounded-xl pr-4 lg:pr-8 opacity-50 translate-y-5 invisible bg-white w-full max-w-[492px] absolute top-[42px] lg:top-[49px] right-0 shadow-product-slider-shadow',
                    openBankList && 'opacity-100 translate-y-0 visible z-[510]',
                    !openBankList && '-z-10',
                  )}
                >
                  <div className="px-8 py-20 rounded-xl max-h-[552px] overflow-y-auto">
                    {filteredBanks.length > 0 ? (
                      filteredBanks.map((item, index) => {
                        const isLast = index === filteredBanks.length - 1;

                        return (
                          <div
                            role="presentation"
                            key={item?.id}
                            className={cn(
                              'flex items-center gap-4 px-8 py-12 cursor-pointer border-b border-aquarelle-purple',
                              {
                                '!border-b-0 !border-transparent': isLast,
                              },
                            )}
                            onClick={() => {
                              handleSelectBank(item);
                              setOpenBankList(false);
                            }}
                          >
                            <div className="relative h-[36px] w-[36px] lg:w-[40px] lg:h-[40px]">
                              <ImageComponentV2
                                src={
                                  item?.logo ? getImageUrl(item?.logo) : null
                                }
                                alt={item?.name}
                                className="w-full h-full max-w-full rounded-full object-contain"
                                type={imageTypes.icon}
                                width={40}
                                height={36}
                                loading="lazy"
                              />
                            </div>
                            <TextBase
                              text={item?.name}
                              className="text-coarse-wool font-medium ml-8 mr-16"
                            />
                          </div>
                        );
                      })
                    ) : (
                      <TextBase
                        text="No item found!"
                        className="p-2 text-center"
                      />
                    )}
                  </div>
                </div>
              </div>

              <TextBase
                text={`Minimum Purchase: ${currentCurrency} ${formatPrice(minEMIPrice)}`}
                className="text-coarse-wool font-medium mb-[24px]"
              />

              <EmiTable
                className="lg:hidden"
                tenures={tenureDetails ?? []}
                isCart={data?.isCart}
                price={data?.price}
              />

              <EmiTableDesktop
                className="hidden lg:block overflow-y-auto custom-scrollbar-store h-auto max-h-[calc(100vh-388px)] pr-8"
                tenures={tenureDetails ?? []}
                isCart={data?.isCart}
                price={data?.price}
              />
            </div>
          </div>
        </div>
      </div>
    </ModalWrapper>
  );
}

export default EmiTableModal;
